/*
 * Mobile pinch / double-tap / drag zoom for the product media modal.
 *
 * Desktop keeps the existing hover magnify (magnify.js); this only activates
 * on touch-sized viewports. It transforms the active modal image with
 * translate + scale, clamps panning to the image bounds, and resets when the
 * modal closes or the visible photo changes.
 */
(function () {
  const MQ = window.matchMedia('(max-width: 749px)');
  const MAX_SCALE = 4;
  const DOUBLE_TAP_SCALE = 2.5;
  const DOUBLE_TAP_MS = 300;
  const HIRES_SCALE = 2; // swap to a sharper source once zoomed past this

  class TouchZoom {
    constructor(content) {
      this.content = content;
      this.img = null;
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.pointers = new Map();
      this.pinch = null;
      this.lastTap = 0;
      this.panStart = null;

      this.onDown = this.onDown.bind(this);
      this.onMove = this.onMove.bind(this);
      this.onUp = this.onUp.bind(this);

      content.addEventListener('pointerdown', this.onDown);
      content.addEventListener('pointermove', this.onMove);
      content.addEventListener('pointerup', this.onUp);
      content.addEventListener('pointercancel', this.onUp);
      content.addEventListener('pointerleave', this.onUp);
    }

    activeImage() {
      const active = this.content.querySelector('.active');
      if (!active) return null;
      return active.tagName === 'IMG' ? active : active.querySelector('img');
    }

    apply() {
      if (!this.img) return;
      this.img.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
      this.img.classList.toggle('is-zoomed', this.scale > 1);
    }

    reset(img) {
      const target = img || this.img;
      if (target) {
        target.style.transition = '';
        target.style.transform = '';
        target.classList.remove('is-zoomed');
        // Restore the responsive source after a hi-res swap.
        if (target._origSrcset != null) {
          target.setAttribute('srcset', target._origSrcset);
          if (target._origSizes != null) target.setAttribute('sizes', target._origSizes);
          if (target._origSrc) target.src = target._origSrc;
          target._origSrcset = null;
          target._hiResLoaded = false;
        }
      }
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.pinch = null;
      this.panStart = null;
    }

    // Swap in a sharper source for deep zoom, sized to the zoomed pixel demand.
    loadHiRes() {
      const img = this.img;
      if (!img || img._hiResLoaded) return;
      const srcset = img.getAttribute('srcset');
      if (!srcset) return;
      const candidates = srcset
        .split(',')
        .map((part) => part.trim().split(/\s+/))
        .map(([url, w]) => ({ url, w: parseInt(w, 10) }))
        .filter((c) => c.url && c.w)
        .sort((a, b) => a.w - b.w);
      if (!candidates.length) return;

      img._hiResLoaded = true;
      const desired = img.clientWidth * MAX_SCALE * (window.devicePixelRatio || 1);
      const pick = candidates.find((c) => c.w >= desired) || candidates[candidates.length - 1];
      if (pick.w <= img.clientWidth * (window.devicePixelRatio || 1)) return; // already adequate

      img._origSrcset = srcset;
      img._origSizes = img.getAttribute('sizes');
      img._origSrc = img.currentSrc || img.src;

      const pre = new Image();
      pre.onload = () => {
        if (!img._hiResLoaded) return; // reset happened before load finished
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = pick.url;
      };
      pre.src = pick.url;
    }

    // Clamp translation so the (scaled) image edges never pull inside the frame.
    clamp() {
      const rect = this.content.getBoundingClientRect();
      const maxX = Math.max(0, (this.img.clientWidth * this.scale - rect.width) / 2);
      const maxY = Math.max(0, (this.img.clientHeight * this.scale - rect.height) / 2);
      this.tx = Math.min(maxX, Math.max(-maxX, this.tx));
      this.ty = Math.min(maxY, Math.max(-maxY, this.ty));
    }

    // Zoom toward a screen-space focal point, keeping that point under the finger.
    zoomTo(newScale, focalX, focalY) {
      newScale = Math.min(MAX_SCALE, Math.max(1, newScale));
      const rect = this.content.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const fx = focalX - cx;
      const fy = focalY - cy;
      const ratio = newScale / this.scale;
      this.tx = fx - ratio * (fx - this.tx);
      this.ty = fy - ratio * (fy - this.ty);
      this.scale = newScale;
      if (this.scale === 1) {
        this.tx = 0;
        this.ty = 0;
      }
      this.clamp();
      this.apply();
      if (this.scale >= HIRES_SCALE) this.loadHiRes();
    }

    onDown(event) {
      this.img = this.activeImage();
      if (!this.img) return;
      this.img.style.transition = '';
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 2) {
        const pts = [...this.pointers.values()];
        this.pinch = {
          startDist: this.distance(pts[0], pts[1]),
          startScale: this.scale,
        };
        this.panStart = null;
      } else if (this.pointers.size === 1) {
        // Double-tap detection
        const now = event.timeStamp;
        if (now - this.lastTap < DOUBLE_TAP_MS) {
          this.lastTap = 0;
          this.img.style.transition = 'transform 0.25s ease';
          if (this.scale > 1) {
            this.reset();
            this.apply();
          } else {
            this.zoomTo(DOUBLE_TAP_SCALE, event.clientX, event.clientY);
          }
          return;
        }
        this.lastTap = now;
        if (this.scale > 1) {
          this.panStart = { x: event.clientX, y: event.clientY, tx: this.tx, ty: this.ty };
        }
      }
    }

    onMove(event) {
      if (!this.img || !this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pinch && this.pointers.size === 2) {
        event.preventDefault();
        const pts = [...this.pointers.values()];
        const dist = this.distance(pts[0], pts[1]);
        const midX = (pts[0].x + pts[1].x) / 2;
        const midY = (pts[0].y + pts[1].y) / 2;
        this.zoomTo((dist / this.pinch.startDist) * this.pinch.startScale, midX, midY);
      } else if (this.panStart && this.scale > 1) {
        event.preventDefault();
        this.tx = this.panStart.tx + (event.clientX - this.panStart.x);
        this.ty = this.panStart.ty + (event.clientY - this.panStart.y);
        this.clamp();
        this.apply();
      }
    }

    onUp(event) {
      this.pointers.delete(event.pointerId);
      if (this.pointers.size < 2) this.pinch = null;
      if (this.pointers.size === 0) this.panStart = null;
    }

    distance(a, b) {
      return Math.hypot(a.x - b.x, a.y - b.y);
    }
  }

  function init(modal) {
    const content = modal.querySelector('[role="document"]');
    if (!content || content.touchZoom) return;
    const zoom = new TouchZoom(content);
    content.touchZoom = zoom;

    // Reset when the visible photo changes.
    new MutationObserver(() => {
      content.querySelectorAll('img').forEach((img) => {
        if (!img.classList.contains('active') && img.style.transform) zoom.reset(img);
      });
      if (zoom.img && !zoom.img.classList.contains('active')) zoom.reset(zoom.img);
    }).observe(content, { attributes: true, attributeFilter: ['class'], subtree: true });

    // Reset when the modal closes.
    new MutationObserver(() => {
      if (!modal.hasAttribute('open')) {
        content.querySelectorAll('img').forEach((img) => zoom.reset(img));
      }
    }).observe(modal, { attributes: true, attributeFilter: ['open'] });
  }

  function boot() {
    if (!MQ.matches) return;
    document.querySelectorAll('product-modal').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  MQ.addEventListener('change', boot);
})();
